import {
  Button,
  Input,
  Label as RacLabel,
  Popover,
  SearchField,
  Select,
} from 'react-aria-components';
import styled, { css } from 'styled-components';

const sharedInputStyles = css`
  padding: var(--spacing-small) var(--spacing-base);
  border: 1px solid var(--grey-300);
  border-radius: var(--radius-large);
  box-shadow: var(--shadow-xs);
  line-height: 24px;
  font-size: var(--font-size-text-md);
  font-weight: var(--font-weight-regular);

  &[data-focus-visible],
  &[data-focused] {
    box-shadow: var(--shadow-focus);
    border: 1px solid var(--brand-600);
    outline: 0;
  }

  &[data-disabled] {
    background-color: var(--grey-50);
    color: var(--grey-500);
  }

  &[data-invalid] {
    border-color: var(--error-300);
    box-shadow: var(--shadow-error);
    color: var(--grey-500);
  }
`;

export const Field = styled(Select)<{ $width?: number }>`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-small);
  width: 100%;
  max-width: 422px;

  & .react-aria-Text {
    font-size: var(--font-size-text-sm);
    font-weight: var(--font-weight-regular);
    color: var(--grey-600);
  }

  & .react-aria-FieldError {
    font-size: var(--font-size-text-sm);
    font-weight: var(--font-weight-bold);
    color: var(--error-600);
  }
`;

export const Label = styled(RacLabel)`
  font-size: var(--font-size-text-sm);
  font-weight: var(--font-weight-medium);
  color: var(--grey-700);
`;

export const Trigger = styled(Button)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  height: 44px;
  background: transparent;
  ${sharedInputStyles}

  &:is([data-invalid] &) {
    border-color: var(--error-300);
    box-shadow: var(--shadow-error);
    color: var(--grey-500);
  }

  & svg {
    width: 10px;
    height: 10px;
  }
`;

export const Overlay = styled(Popover)`
  display: flex;
  flex-direction: column;
  ${sharedInputStyles}
  width: var(--trigger-width);
  background: var(--white);

  & [role="listbox"] {
    overflow: auto;

    & .react-aria-ListBoxItem {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      padding: var(--spacing-small);
      border-radius: var(--radius-large);

      &[data-focus-visible],
      &[data-focused] {
        background: var(--brand-50);
        outline: 0;
      }

      &[data-selected] {
        background: var(--brand-50);

        & path {
          stroke: var(--brand-800);
        } 
      }

      & svg {
        width: 18px;
        height: 18px;
      }
    }
  }
`;

export const Filter = styled(SearchField)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: var(--spacing-small);
  ${sharedInputStyles}
  height: 44px;

  &:has([data-focus-visible], [data-focused]) {
    box-shadow: var(--shadow-focus);
    border: 1px solid var(--brand-600);
    outline: 0;

    &[data-focus-visible],
    &[data-focused] {
      outline: 0;
    }
  }

  & svg {
    width: 14px;
    height:14px;
  }

  & button {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 20px;
    height: 20px;
    border: 0;
    border-radius: var(--radius-fullyrounded);

    &:is([data-empty] > &) {
      visibility: hidden;
    }
  }
`;

export const SearchInput = styled(Input)`
  flex: 1;
  border: none;
  outline: 0;
`;
